<script setup lang="ts">
import { computed, ref } from 'vue'
import dayjs from 'dayjs'
import { Color } from '@previo/codebooks'
import { $t } from '@previo/core'
import { useDateTimePicker } from '../../../composables/DateTimePickerState'
import PIcon from '../../../../icon/PIcon.vue'
import PTextStandard from '../../../../typography/body/PTextStandard.vue'
import { PContextMenuPositionEnum } from '../../../../context-menu/types'
import PContextMenu from '../../../../context-menu/PContextMenu.vue'

type Emits = {
  (e: 'select'): void
}

const emits = defineEmits<Emits>()

const navigation = useDateTimePicker()
const year = ref<number>(navigation.currentView.value.year() || dayjs().year())

const isYearNavigationVisible = ref<boolean>(false)

const startYear = ref<number>(year.value - 10)
const years = computed(() => {
  return Array.from({ length: 21 }, (_, i) => startYear.value + i)
})

const navigateYears = (years: number): void => {
  startYear.value += years
}

const selectYear = (year: number): void => {
  emits('select')

  navigation.currentView.value = navigation.currentView.value.year(year)
  isYearNavigationVisible.value = false
}

const open = (withYear: number): void => {
  year.value = withYear
  isYearNavigationVisible.value = true
}

const toggle = (withYear: number): void => {
  year.value = withYear
  isYearNavigationVisible.value = !isYearNavigationVisible.value
}

defineExpose({
  open,
  toggle,
})
</script>

<template>
  <p-context-menu
    v-model="isYearNavigationVisible"
    :title="$t('Select year')"
    :anchor="PContextMenuPositionEnum.BOTTOM_MIDDLE"
    :self="PContextMenuPositionEnum.TOP_MIDDLE"
    cy="date-time-picker-year-navigation"
  >
    <div class="ds-date-time-picker__year-navigation">
      <p-icon icon="chevron-left" cursor-pointer @click="navigateYears(-21)" />
      <div class="ds-date-time-picker__year-grid">
        <button
          v-for="yearIndex in years"
          :key="yearIndex"
          :class="[
            'ds-date-time-picker__year-button p-flex p-flex-center p-cursor-pointer p-non-selectable',
            {
              'ds-date-time-picker__year-button--selected': year === yearIndex,
            },
          ]"
          @click="selectYear(yearIndex)"
        >
          <p-text-standard
            :text="yearIndex"
            :color="year === yearIndex ? Color.WHITE : Color.GREY_500"
          />
        </button>
      </div>
      <p-icon icon="chevron-right" cursor-pointer @click="navigateYears(21)" />
    </div>
  </p-context-menu>
</template>

<style scoped lang="scss">
@import '../../../../../assets/scss/shared/variables/ds.radius';
@import '../../../../../assets/scss/shared/brand.module';

.ds-date-time-picker {
  &__year-navigation {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
  }

  &__year-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
  }

  &__year-button {
    height: 48px;
    border-radius: $radius-xl-mobile;
    border: none;
    outline: none;

    background-color: getColor('white') !important;

    &--selected {
      background-color: getColor('primary') !important;
    }

    &:hover:not(&--selected) {
      background-color: #f5f5f5;
    }
  }
}
</style>
